import Navbar from "../components/Navbar";
import { Link } from "react-router-dom";
import { PawPrint, ShieldCheck, HeartHandshake, Sparkles, ChevronDown } from "lucide-react";
import { useState } from "react";

const FAQ = [
  {
    q: "How old do I have to be to volunteer?",
    a: "You must be 18+ to volunteer. Those under 18 are required to have parent or guardian permission and must be accompanied by an adult.",
  },
  {
    q: "What should I wear?",
    a: "Closed-toe shoes with good grip (no sandals), long pants or sturdy shorts, and layers you don't mind getting muddy or shed on. We provide leashes and waste bags.",
  },
  {
    q: "What if I've never handled shelter dogs before?",
    a: "That's totally fine! Your first shift will be paired with an experienced volunteer or staff member who'll show you around the kennels, walk you through the leash protocol, and introduce you to the dogs you'll be working with.",
  },
  {
    q: "Can I cancel a shift?",
    a: "Yes. Please cancel from your dashboard as early as possible so we can offer the spot to another volunteer. Frequent last-minute cancellations may pause your account.",
  },
  {
    q: "Do volunteer hours count toward school or work programs?",
    a: "They do. We can sign off on hours for school service requirements, workplace giving programs, and Court Ordered Community Service. Ask the shift lead to log and stamp your paperwork before you leave.",
  },
  {
    q: "How do you notify me when help is needed?",
    a: "You'll get an email plus an in-app notification when the shelter urgently needs a shift filled. You can also opt into a morning-of reminder email for each shift you're signed up for.",
  },
  {
    q: "Where is the shelter?",
    a: "Hood River Adopt A Dog · 3910 Heron Drive, Hood River, OR 97031. Directions and gate code are sent by email when you're confirmed for a shift.",
  },
];

const PROTOCOLS = [
  {
    icon: <ShieldCheck className="h-5 w-5" />,
    title: "Kennel entry protocol",
    body: "Wash hands or use hand sanitizer between kennels. Never enter a kennel if a red card is on the door (medical hold or behavior evaluation).",
  },
  {
    icon: <HeartHandshake className="h-5 w-5" />,
    title: "Walking on the property",
    body: "One dog per leash, always. Six-foot leash minimum. Keep at least a full car-length between dogs when passing another walker.",
  },
  {
    icon: <PawPrint className="h-5 w-5" />,
    title: "Feeding and treats",
    body: "Check each dog's whiteboard for food allergies before offering anything — including treats. No people food, no rawhides, no bones. Fresh water bowls at every kennel every shift.",
  },
  {
    icon: <Sparkles className="h-5 w-5" />,
    title: "Walking a dog",
    body: "Have a nice relaxed walk, preferably an hour or at least 30 minutes for each dog you wish to exercise for the day. When returning please keep a safe distance from other walkers and the door as staff may be coming through with a dog. If you wish to volunteer more time, ask for a 2nd walking buddy!",
  },
];

function Item({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="card-warm p-5" data-testid={`faq-${q.slice(0,20).replace(/\s+/g,'-').toLowerCase()}`}>
      <button onClick={()=>setOpen(!open)} className="w-full flex items-center justify-between text-left gap-4">
        <span className="font-display text-base">{q}</span>
        <ChevronDown className={`h-5 w-5 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="mt-3 text-sm text-[color:var(--stone)] leading-relaxed">{a}</p>}
    </div>
  );
}

export default function Handbook() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-5xl mx-auto px-5 lg:px-10 py-12">
        <div className="tag-pill tag-yellow">Handbook</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 leading-tight">Everything a new volunteer<br/>should know.</h1>
        <p className="text-[color:var(--stone)] mt-4 max-w-2xl">A quick read before your first shift — kennel protocols, the FAQ we hear the most, and what to bring. Come back anytime.</p>

        {/* Kennel protocols */}
        <h2 className="font-display text-2xl mt-14 mb-5">Kennel & safety protocols</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {PROTOCOLS.map((p) => (
            <div key={p.title} className="card-warm p-6" data-testid={`protocol-${p.title.replace(/\s+/g,'-').toLowerCase()}`}>
              <div className="h-10 w-10 rounded-full bg-[color:var(--cream-2)] flex items-center justify-center mb-3">{p.icon}</div>
              <div className="font-display text-lg">{p.title}</div>
              <p className="text-sm text-[color:var(--stone)] mt-2 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>

        {/* FAQ */}
        <h2 className="font-display text-2xl mt-14 mb-5">Frequently asked</h2>
        <div className="space-y-3">
          {FAQ.map((f) => <Item key={f.q} q={f.q} a={f.a} />)}
        </div>

        {/* CTA */}
        <div className="mt-14 rounded-3xl bg-[color:var(--yellow)] p-8 sm:p-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-display text-2xl sm:text-3xl max-w-md leading-tight">Still have a question we didn&rsquo;t cover?</h3>
            <p className="mt-2 text-sm text-[color:var(--ink)]/80">Email us at volunteers@hoodriveradoptadog.org and we&rsquo;ll get right back.</p>
          </div>
          <Link to="/shifts" data-testid="handbook-cta-btn" className="btn-pill btn-primary text-sm">Browse open shifts</Link>
        </div>
      </div>
      <footer className="border-t border-[color:var(--border)] py-8 text-center text-xs text-[color:var(--stone)] sr-only">© Hood River Adopt A Dog · Volunteer Portal</footer>
    </div>
  );
}
